// =====================================================
// SERVICE: BOOKING & PAIEMENT SANTÉ PSY ONLINE
// Date: 2025-10-02
// Description: Gestion complète de la réservation avec paiement
// =====================================================

import { supabase } from '../lib/supabase';
import type { BookingData, BookingResult } from '../types/psy.types';
import { createOrUpdatePatient } from './psyPatientService';
import { getPractitionerById } from './psyPractitionerService';
import { createAppointment, isSlotAvailable } from './psyAppointmentService';

const CAMPAY_ACCESS_TOKEN = import.meta.env.VITE_CAMPAY_ACCESS_TOKEN;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// =====================================================
// BOOKING COMPLET
// =====================================================

/**
 * Crée un rendez-vous avec paiement CamPay
 */
export const createAppointmentWithPayment = async (
  bookingData: BookingData
): Promise<BookingResult> => {
  try {
    // 1. Vérifier que le créneau est toujours disponible
    const available = await isSlotAvailable(
      bookingData.practitionerId,
      bookingData.scheduledAt,
      bookingData.durationMinutes
    );

    if (!available) {
      throw new Error('Ce créneau n\'est plus disponible');
    }

    // 2. Créer ou récupérer le patient
    const patient = await createOrUpdatePatient(bookingData.patientData);

    // 3. Récupérer le tarif du praticien
    const practitioner = await getPractitionerById(bookingData.practitionerId);
    const amount = practitioner.base_rate_per_session;

    // 4. Créer le rendez-vous (status=scheduled, payment=pending)
    const appointment = await createAppointment({
      practitioner_id: bookingData.practitionerId,
      patient_id: patient.id,
      scheduled_at: bookingData.scheduledAt,
      duration_minutes: bookingData.durationMinutes,
      consultation_mode: bookingData.consultationMode,
      reason_for_visit: bookingData.reasonForVisit,
      amount_paid: amount,
      payment_method:
        bookingData.paymentMethod === 'mtn'
          ? 'mtn_mobile_money'
          : 'orange_money',
    });

    // 5. Initier le paiement CamPay
    const paymentResult = await initiateCamPayPayment({
      amount,
      phone: bookingData.paymentPhone,
      provider: bookingData.paymentMethod,
      reference: appointment.id,
      description: `Consultation avec ${practitioner.full_name}`,
    });

    // 6. Mettre à jour le rendez-vous avec la référence de paiement
    await supabase
      .from('psy_appointments')
      .update({
        transaction_reference: paymentResult.reference,
      })
      .eq('id', appointment.id);

    return {
      appointment,
      paymentReference: paymentResult.reference,
      paymentUrl: paymentResult.payment_url,
    };
  } catch (error) {
    console.error('Erreur booking:', error);
    throw error;
  }
};

// =====================================================
// PAIEMENT CAMPAY
// =====================================================

interface CamPayPaymentData {
  amount: number;
  phone: string;
  provider: 'mtn' | 'orange';
  reference: string;
  description: string;
}

interface CamPayPaymentResult {
  reference: string;
  payment_url?: string;
  status: string;
}

/**
 * Initie un paiement via CamPay
 */
const initiateCamPayPayment = async (
  data: CamPayPaymentData
): Promise<CamPayPaymentResult> => {
  try {
    const response = await fetch('https://api.campay.net/collect', {
      method: 'POST',
      headers: {
        'Authorization': `Token ${CAMPAY_ACCESS_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: data.amount.toString(),
        currency: 'XAF',
        from: data.phone,
        description: data.description,
        external_reference: data.reference,
        external_user: data.phone,
        redirect_url: `${window.location.origin}/psy/paiement/confirmation`,
        webhook_url: `${SUPABASE_URL}/functions/v1/campay-psy-webhook`,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur paiement CamPay');
    }

    const result = await response.json();

    return {
      reference: result.reference || data.reference,
      payment_url: result.link,
      status: result.status,
    };
  } catch (error) {
    console.error('Erreur CamPay:', error);
    throw error;
  }
};

// =====================================================
// VÉRIFICATION DISPONIBILITÉ
// =====================================================

/**
 * Récupère les créneaux disponibles pour un praticien sur une période
 */
export const getAvailableSlots = async (
  practitionerId: string,
  startDate: Date,
  endDate: Date
): Promise<string[]> => {
  // TODO: Implémenter la logique complète avec psy_availabilities
  // Pour l'instant, retourne des créneaux par défaut

  const slots: string[] = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    // Générer des créneaux de 9h à 17h (chaque heure)
    for (let hour = 9; hour < 17; hour++) {
      const slotDate = new Date(currentDate);
      slotDate.setHours(hour, 0, 0, 0);

      // Vérifier si le créneau est disponible
      const available = await isSlotAvailable(
        practitionerId,
        slotDate.toISOString(),
        60 // 60 minutes par défaut
      );

      if (available) {
        slots.push(slotDate.toISOString());
      }
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return slots;
};

/**
 * Récupère les créneaux disponibles pour aujourd'hui
 */
export const getTodayAvailableSlots = async (practitionerId: string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return getAvailableSlots(practitionerId, today, tomorrow);
};

/**
 * Récupère les créneaux disponibles pour cette semaine
 */
export const getWeekAvailableSlots = async (practitionerId: string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);

  return getAvailableSlots(practitionerId, today, nextWeek);
};

// =====================================================
// CALCUL REMBOURSEMENT
// =====================================================

/**
 * Calcule le montant du remboursement selon la politique d'annulation
 */
export const calculateRefundAmount = (
  appointmentDate: string,
  amountPaid: number
): number => {
  const now = new Date();
  const scheduledAt = new Date(appointmentDate);
  const hoursUntilAppointment = (scheduledAt.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (hoursUntilAppointment >= 24) {
    return amountPaid; // 100% remboursé
  } else if (hoursUntilAppointment >= 6) {
    return amountPaid * 0.5; // 50% remboursé
  } else {
    return 0; // 0% remboursé
  }
};

/**
 * Formate le montant en FCFA
 */
export const formatAmountFCFA = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XAF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(amount)
    .replace('XAF', 'FCFA');
};
